import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from "../../theme/buttonstyles";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";

const EMPTY_EVENT = {
  name: "",
  event_type: "",
  status: "planned",
  starts_at: "",
  ends_at: "",
  venue_name: "",
  address: "",
  city: "",
  state: "",
  host_organization: "",
  registration_fee: "",
  capacity: "",
  target_audience: "",
  program_area: "",
  description: "",
};

const STATUS_OPTIONS = ["planned", "open", "completed", "cancelled"];

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    color: BRAND_BLUE,
    background: "#ffffff",
    "& fieldset": { border: "1px solid #cbd5e1" },
    "&:hover fieldset": { borderColor: BRAND_BLUE },
    "&.Mui-focused fieldset": { borderColor: BRAND_BLUE, borderWidth: 2 },
  },
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
    color: "#64748b",
    fontWeight: 600,
  },
};

// MUI v7's Grid dropped the item/xs/sm/md breakpoint props in favor of `size`;
// a plain CSS grid avoids relying on that API and keeps spans explicit.
const FORM_ROW_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(12, 1fr)" },
  gap: "16px",
};

function Field({ span, children }) {
  return <Box sx={{ gridColumn: { xs: "span 1", sm: `span ${span}` } }}>{children}</Box>;
}

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN }} />
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{children}</Typography>
    </Box>
  );
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function buildPayload(values) {
  return {
    name: values.name,
    event_type: values.event_type || null,
    status: values.status || "planned",
    starts_at: values.starts_at ? new Date(values.starts_at).toISOString() : null,
    ends_at: values.ends_at ? new Date(values.ends_at).toISOString() : null,
    venue_name: values.venue_name || null,
    address: values.address || null,
    city: values.city || null,
    state: values.state || null,
    host_organization: values.host_organization || null,
    registration_fee: values.registration_fee === "" ? null : Number(values.registration_fee),
    capacity: values.capacity === "" ? null : Number(values.capacity),
    target_audience: values.target_audience || null,
    program_area: values.program_area || null,
    description: values.description || null,
  };
}

export default function MembershipEventDialog({
  open,
  event,
  saving,
  onClose,
  onSubmit,
}) {
  const [values, setValues] = useState(EMPTY_EVENT);

  useEffect(() => {
    if (!open) return;
    setValues({
      ...EMPTY_EVENT,
      ...Object.fromEntries(
        Object.entries(event || {}).map(([key, value]) => [key, value ?? ""]),
      ),
      starts_at: toDateTimeLocal(event?.starts_at),
      ends_at: toDateTimeLocal(event?.ends_at),
    });
  }, [event, open]);

  const setValue = (field) => (e) =>
    setValues((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${BRAND_BLUE}`,
          boxShadow: "0 18px 54px rgba(0,51,108,0.20)",
        },
      }}
    >
      <DialogTitle sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
        {event?.id ? "Edit Event" : "Create Event"}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fbfcf8" }}>
        <Box sx={{ pt: 1 }}>
          <SectionHeader>Event Details</SectionHeader>
          <Box sx={FORM_ROW_SX}>
            <Field span={8}>
              <TextField label="Event Name" value={values.name} onChange={setValue("name")} fullWidth required sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField select label="Status" value={values.status} onChange={setValue("status")} fullWidth sx={FIELD_SX}>
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Field>
            <Field span={4}>
              <TextField label="Event Type" value={values.event_type} onChange={setValue("event_type")} fullWidth sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField label="Event Date" type="datetime-local" value={values.starts_at} onChange={setValue("starts_at")} fullWidth InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
            </Field>
            <Field span={4}>
              <TextField label="Ends At" type="datetime-local" value={values.ends_at} onChange={setValue("ends_at")} fullWidth InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
            </Field>
          </Box>

          <Box sx={{ mt: 3 }}>
            <SectionHeader>Location</SectionHeader>
            <Box sx={FORM_ROW_SX}>
              <Field span={6}>
                <TextField label="Venue" value={values.venue_name} onChange={setValue("venue_name")} fullWidth sx={FIELD_SX} />
              </Field>
              <Field span={3}>
                <TextField label="City" value={values.city} onChange={setValue("city")} fullWidth sx={FIELD_SX} />
              </Field>
              <Field span={3}>
                <TextField label="State" value={values.state} onChange={setValue("state")} fullWidth sx={FIELD_SX} />
              </Field>
              <Field span={12}>
                <TextField label="Address" value={values.address} onChange={setValue("address")} fullWidth sx={FIELD_SX} />
              </Field>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <SectionHeader>Logistics & Audience</SectionHeader>
            <Box sx={FORM_ROW_SX}>
              <Field span={4}>
                <TextField label="Host Organization" value={values.host_organization} onChange={setValue("host_organization")} fullWidth sx={FIELD_SX} />
              </Field>
              <Field span={4}>
                <TextField label="Registration Fee" type="number" value={values.registration_fee} onChange={setValue("registration_fee")} fullWidth sx={FIELD_SX} />
              </Field>
              <Field span={4}>
                <TextField label="Capacity" type="number" value={values.capacity} onChange={setValue("capacity")} fullWidth sx={FIELD_SX} />
              </Field>
              <Field span={6}>
                <TextField label="Target Audience" value={values.target_audience} onChange={setValue("target_audience")} fullWidth sx={FIELD_SX} />
              </Field>
              <Field span={6}>
                <TextField label="Related Program Area" value={values.program_area} onChange={setValue("program_area")} fullWidth sx={FIELD_SX} />
              </Field>
              <Field span={12}>
                <TextField
                  label="Description"
                  value={values.description}
                  onChange={setValue("description")}
                  fullWidth
                  multiline
                  minRows={3}
                  sx={FIELD_SX}
                />
              </Field>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={SECONDARY_BUTTON_SX}>Cancel</Button>
        <Button
          onClick={() => onSubmit(buildPayload(values))}
          disabled={saving || !values.name.trim()}
          sx={PRIMARY_BUTTON_SX}
        >
          {saving ? "Saving..." : "Save Event"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
